import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Settings,
  Bot,
  ImageIcon,
  Loader2,
  HelpCircle,
  Clock,
  User,
} from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { usePermissions } from '../context/PermissionContext';
import { supabase } from '../lib/supabase';
import { retrySupabase } from '../utils/retry';
import { mergeLastCrearAnuncioState } from '../utils/crearAnuncioStorage';

// Sidebar aplanada: solo pestañas vigentes.
// Accesos en desuso comentados y se indica la categoría original.
// Categorías en desuso (antigua versión):
//  - Ventas (Registro de ventas, Historial de ventas, Mensajes, Clientes, Gráficos de Interacción, Envíos)
//  - Inventario (Lista de productos, Stock, Descuentos)
//  - Compras (Registro de compras, Historial de compras, Forecasting, Proveedores)
//  - Finanzas (Flujo de caja, Forecasting financiero, Gastos recurrentes)

type FlatMenuItem = {
  name: string;
  icon: React.ReactNode;
  path: string;
  permissionKey: string; // clave en PermissionContext
  // categoría original (solo para referencia, no visible)
  category: 'Generación de imágenes' | 'Empleado IA';
  betaOnly?: boolean;
};

  // Pestañas vigentes (sin separación visible). Comentado en qué categoría estaban.
  const FLAT_MENU_ITEMS: FlatMenuItem[] = [
    // Generación de imágenes
    { name: 'Estudio Virtual', icon: <ImageIcon size={20} />, path: '/redes-sociales/crear-anuncio', permissionKey: 'crear-anuncio', category: 'Generación de imágenes' },
    { name: 'Test V1', icon: <Bot size={20} />, path: '/test-v1', permissionKey: 'test-v1', category: 'Generación de imágenes', betaOnly: true },
    { name: 'Historial', icon: <Clock size={20} />, path: '/redes-sociales/imagenes', permissionKey: 'imagenes', category: 'Generación de imágenes' },
    // Empleado IA
    { name: 'Manager de Redes IA', icon: <Bot size={20} />, path: '/redes-sociales/calendario', permissionKey: 'calendario', category: 'Empleado IA', betaOnly: true },
  ];

interface UserProfile {
  id: string;
  name: string;
  email: string;
  id_company: string | null;
  company_name?: string | null;
  company_logo?: string | null;
  beta_tester?: boolean | null;
  role: string | null;
}

export const Sidebar = () => {
  const location = useLocation();
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileNavWidth, setMobileNavWidth] = useState<number>(0);
  const panelRef = useRef<HTMLDivElement | null>(null);

  // Helper: blur currently focused element if it lives inside the mobile panel
  const blurFocusInsidePanel = () => {
    try {
      const active = document.activeElement as HTMLElement | null;
      if (active && panelRef.current && panelRef.current.contains(active)) {
        active.blur();
      }
    } catch {
      // noop
    }
  };

  // Clear Crear flow selected products when navigating via navbar/sidebar
  const clearCrearSelection = React.useCallback(() => {
    try {
      const isInCrearFlow = location.pathname.startsWith('/redes-sociales/crear-anuncio');
      if (isInCrearFlow) {
        mergeLastCrearAnuncioState({ selectedVariantIds: [] });
      }
    } catch {
      // noop
    }
  }, [location.pathname]);

  // Get user permissions
  const { permissions } = usePermissions();
  
  // Siempre remover espaciado lateral de sidebar (ya no se usa en desktop)
  useEffect(() => {
    document.documentElement.classList.add('no-sidebar-spacing');
    document.body.classList.add('no-sidebar-spacing');
    return () => {
      // mantener la clase para todo el app
      document.documentElement.classList.add('no-sidebar-spacing');
      document.body.classList.add('no-sidebar-spacing');
    };
  }, []);
  
  // Compute dynamic mobile nav width (around 82vw capped to 320px)
  useEffect(() => {
    const updateWidth = () => {
      const vw = typeof window !== 'undefined' ? window.innerWidth : 0;
      // Narrower sidebar: ~74vw capped at 280px (before: 82vw capped at 320px)
      const width = Math.min(280, Math.round(vw * 0.74));
      setMobileNavWidth(width);
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);
  
  // Sync internal state when other components dispatch the same toggle event
  useEffect(() => {
    const handler = (e: Event) => {
      try {
        const detail = (e as CustomEvent).detail as any;
        if (typeof detail?.isMobileMenuOpen === 'boolean') {
          setMobileMenuOpen(detail.isMobileMenuOpen);
          if (detail.isMobileMenuOpen === false) {
            // Ensure no element inside the hidden panel retains focus
            blurFocusInsidePanel();
          }
        }
      } catch {
        // noop
      }
    };
    window.addEventListener('sidebar-mobile-toggle' as any, handler as EventListener);
    return () => window.removeEventListener('sidebar-mobile-toggle' as any, handler as EventListener);
  }, []);

  // Apply/Remove inert on the panel to prevent focus while closed
  useEffect(() => {
    try {
      const el = panelRef.current as any;
      if (!el) return;
      if (!mobileMenuOpen) {
        el.setAttribute('inert', '');
      } else {
        el.removeAttribute('inert');
      }
    } catch {
      // noop
    }
  }, [mobileMenuOpen]);
  
  // Fetch user profile including company name
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        setLoading(true);
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data: profile, error } = await retrySupabase(async () =>
          await supabase
            .from('profiles')
            .select('id, name, email, id_company, role')
            .eq('id', user.id)
            .single()
        );

        if (error) throw error;
        
        // If user has a company, fetch the company name and logo from company_profiles table
        if (profile.id_company) {
          const { data: companyData, error: companyError } = await retrySupabase(async () =>
            await supabase
              .from('company_profiles')
              .select('nombre, logo_url, beta_tester')
              .eq('id_company', profile.id_company)
              .single()
          );
            
          if (!companyError && companyData) {
            // Add the company name and logo to the profile object from company_profiles
            const profileWithCompanyInfo = {
              ...profile,
              company_name: companyData.nombre,
              company_logo: companyData.logo_url,
              beta_tester: companyData.beta_tester
            };
            setUserProfile(profileWithCompanyInfo);
            return; // Exit early since we've already set the profile
          }
        }
        
        // If we get here, we either don't have a company or couldn't fetch company data
        setUserProfile(profile);
      } catch (err) {
        console.error('Error fetching user profile:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, []);

  // Se removió el manejo de secciones/categorías para aplanar la navegación

  // Toggle function removed; App.tsx controls mobile menu and dispatches the event.

  // Close mobile menu when a link is clicked
  const closeMobileMenu = () => {
    // Clear focus from any element inside the panel before hiding it
    blurFocusInsidePanel();
    setMobileMenuOpen(false);
    
    // Dispatch event to notify that the mobile menu is closed
    window.dispatchEvent(new CustomEvent('sidebar-mobile-toggle', {
      detail: { isMobileMenuOpen: false }
    }));
  };

  // Mobile nav selection: clear Crear selection (if applicable) and close menu
  const handleMobileNavSelection = () => {
    clearCrearSelection();
    closeMobileMenu();
  };

  // Also close mobile menu when the route changes
  useEffect(() => {
    if (mobileMenuOpen) {
      setMobileMenuOpen(false);
      blurFocusInsidePanel();
      window.dispatchEvent(new CustomEvent('sidebar-mobile-toggle', {
        detail: { isMobileMenuOpen: false }
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Items permitidos según permisos y beta tester (para items marcados betaOnly)
  const isBetaTester = userProfile?.beta_tester === true;
  const permittedFlatItems = FLAT_MENU_ITEMS.filter(item => permissions[item.permissionKey] && (!item.betaOnly || isBetaTester));

  return (
    <>
      {/* Mobile toggle button now lives inside the transformable shell in App.tsx */}

      {/* Mobile Left Panel (revealed by shifting/scaling main content) */}
      <div
        className={
          `fixed inset-y-0 left-0 md:hidden bg-transparent z-20 flex flex-col overflow-y-auto 
           transform transition-transform duration-300 ease-out ${mobileMenuOpen ? 'pointer-events-auto' : 'pointer-events-none'}`
        }
        style={{
          width: mobileNavWidth || 0,
          transform: mobileMenuOpen ? 'translate3d(0,0,0)' : 'translate3d(-100%,0,0)'
        }}
        aria-hidden={!mobileMenuOpen}
        ref={panelRef}
      >
        {/* User profile at top (moved here) */}
        <div className="p-4">
          {loading ? (
            <div className="flex justify-center">
              <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
            </div>
          ) : userProfile ? (
            <div className="bg-[#EFF0F5] rounded-md p-3 flex items-center gap-3">
              <img
                src={userProfile.company_logo ? `${userProfile.company_logo}?width=40&height=40` : "https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system//Logo.svg"}
                alt="User profile"
                className="w-10 h-10 rounded-full object-cover bg-white"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = "https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system//Logo.svg";
                }}
              />
              <div>
                <div className="font-medium">{userProfile.name || 'Usuario'}</div>
                {userProfile.company_name && (
                  <div className="text-sm text-gray-500">{userProfile.company_name}</div>
                )}
              </div>
            </div>
          ) : null}
        </div>

        {/* Mobile Navigation - centered vertically with even spacing */}
        <nav className={`flex-1 px-4 py-2 flex flex-col justify-center gap-2.5`}>
          {/* Tabs vigentes (aplanadas) */}
          <div className="flex flex-col gap-2.5">
            {permittedFlatItems.map((item: FlatMenuItem) => (
              <NavLink
                key={item.name}
                to={item.path === '/redes-sociales/crear-anuncio'
                  ? '/redes-sociales/crear-anuncio/seleccionar-productos'
                  : item.path}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 text-sm rounded-md transition-colors ${isActive ? 'bg-blue-500 text-white' : 'text-gray-700 hover:bg-[#EFF0F5]'}`
                }
                onClick={handleMobileNavSelection}
              >
                {item.icon}
                <span>{item.name}</span>
              </NavLink>
            ))}
          </div>

          {/* Ayuda - Always visible */}
          <NavLink
            to="/ayuda"
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 text-sm rounded-md transition-colors ${
                isActive
                  ? 'bg-blue-500 text-white'
                  : 'text-gray-700 hover:bg-[#EFF0F5]'
              }`
            }
            onClick={handleMobileNavSelection}
          >
            <HelpCircle size={20} />
            <span>Ayuda</span>
          </NavLink>

          {/* Settings - Always visible regardless of permissions */}
          <NavLink
            to="/configuracion"
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 text-sm rounded-md transition-colors ${
                isActive
                  ? 'bg-blue-500 text-white'
                  : 'text-gray-700 hover:bg-[#EFF0F5]'
              }`
            }
            onClick={handleMobileNavSelection}
          >
            <Settings size={20} />
            <span>Configuración</span>
          </NavLink>
        </nav>

        {/* Brand at bottom with same style as desktop top bar (left-aligned) */}
        <div className="p-8 flex items-center">
          <div className="flex items-center gap-2">
            <img
              src="https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system//Logo.svg"
              alt="Estudio Atlas logo"
              className="w-6 h-6"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system//Logo.svg';
              }}
            />
            <span className="text-blue-500 font-semibold text-lg" style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>Estudio Atlas</span>
          </div>
        </div>
      </div>

      {/* Transparent overlay to capture taps and close (no darken effect) */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-y-0 right-0 bg-transparent md:hidden z-40"
          style={{ left: (mobileNavWidth || 0) + 64 }}
          onClick={closeMobileMenu}
          aria-hidden="true"
        />
      )}

    {/* Desktop Top Bar (reemplaza sidebar en desktop) */}
    <div className="hidden md:flex fixed top-0 left-0 right-0 z-30 w-full bg-[#EFF0F5] h-14">
      <div className="w-full h-14 flex items-center px-4 gap-4">
        {/* Izquierda: logo + marca (wordmark) */}
        <div className="flex items-center gap-2">
          <img
            src="https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system//Logo.svg"
            alt="Estudio Atlas logo"
            className="w-6 h-6"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.src = 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system//Logo.svg';
            }}
          />
          <span className="text-blue-500 font-semibold text-lg" style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>Estudio Atlas</span>
        </div>

        <div className="flex-1" />

      </div>
    </div>

    {/* Desktop Vertical Navigation for main tabs */}
    <div className="hidden md:flex fixed left-4 top-1/2 -translate-y-1/2 z-30">
      <nav className="flex flex-col gap-2.5">
        {permittedFlatItems.map((item: FlatMenuItem) => (
          <NavLink
            key={item.name}
            to={item.path === '/redes-sociales/crear-anuncio'
              ? '/redes-sociales/crear-anuncio/seleccionar-productos'
              : item.path}
            className={({ isActive }) =>
              `w-48 flex items-center gap-2 px-4 h-10 rounded-full transition-colors shadow-sm ${
                isActive ? 'bg-blue-500 text-white' : 'bg-white text-gray-700 hover:bg-[#E2E4EC]'
              }`
            }
            title={item.name}
            onClick={clearCrearSelection}
          >
            {item.icon}
            <span className="text-sm">{item.name}</span>
          </NavLink>
        ))}
        <NavLink
          to="/ayuda"
          className={({ isActive }) =>
            `w-48 flex items-center gap-2 px-4 h-10 rounded-full transition-colors shadow-sm ${
              isActive ? 'bg-blue-500 text-white' : 'bg-white text-gray-700 hover:bg-[#E2E4EC]'
            }`
          }
          title="Ayuda"
          onClick={clearCrearSelection}
        >
          <HelpCircle size={20} />
          <span className="text-sm">Ayuda</span>
        </NavLink>
        <NavLink
          to="/configuracion"
          className={({ isActive }) =>
            `w-48 flex items-center gap-2 px-4 h-10 rounded-full transition-colors shadow-sm ${
              isActive ? 'bg-blue-500 text-white' : 'bg-white text-gray-700 hover:bg-[#E2E4EC]'
            }`
          }
          title="Mi cuenta"
          onClick={clearCrearSelection}
        >
          <User size={20} />
          <span className="text-sm">Mi cuenta</span>
        </NavLink>
      </nav>
    </div>
  </>
);
};